import { z } from 'zod';
import { ApplicantStage } from '../applicant-stage';
import { ReferralStatus } from '../enums';
import { MAX_GRADE_LABEL } from './grade-label';

export const REFERRAL_LIMITS = {
  familyLastName: 120,
  relationship: 500,
  narrative: 6000,
  text: 4000,
  name: 80,
  email: 200,
  phone: 40,
  school: 160,
  place: 120,
  guardians: 2,
  children: 10,
} as const;

export const REFERRAL_STEP_COUNT = 5;

const L = REFERRAL_LIMITS;
const text = (max: number) => z.string().trim().max(max, `At most ${max} characters`).default('');

export const FAMILIARITY_ANSWERS = ['yes', 'no', 'unknown'] as const;
export const Familiarity = z.enum(FAMILIARITY_ANSWERS);
export type Familiarity = z.infer<typeof Familiarity>;
export const FAMILIARITY_LABELS: Record<Familiarity, string> = {
  yes: 'Yes',
  no: 'No',
  unknown: 'Not sure',
};

export const FIRST_CHOICE_ANSWERS = ['yes', 'no', 'uncertain', 'unknown'] as const;
export const FirstChoice = z.enum(FIRST_CHOICE_ANSWERS);
export type FirstChoice = z.infer<typeof FirstChoice>;
export const FIRST_CHOICE_LABELS: Record<FirstChoice, string> = {
  yes: 'Yes',
  no: 'No',
  uncertain: 'Undecided',
  unknown: "I don't know",
};

export const ReferralGuardian = z.object({
  firstName: text(L.name),
  lastName: text(L.name),
  email: text(L.email),
  phone: text(L.phone),
});
export type ReferralGuardian = z.infer<typeof ReferralGuardian>;

const childFields = {
  legalFirstName: text(L.name),
  preferredName: text(L.name),
  middleName: text(L.name),
  lastName: text(L.name),
  grade: text(MAX_GRADE_LABEL),
  currentSchool: text(L.school),
  city: text(L.place),
  stateOrCountry: text(L.place),
  academicFamiliarity: Familiarity.default('unknown'),
  academicContext: text(L.text),
  disciplinaryFamiliarity: Familiarity.default('unknown'),
  disciplinaryContext: text(L.text),
};

export const ReferralChildInput = z.object({
  childKey: z.string().uuid().optional(),
  ...childFields,
});
export type ReferralChildInput = z.infer<typeof ReferralChildInput>;
export const ReferralChild = z.object({ childKey: z.string().uuid(), ...childFields });
export type ReferralChild = z.infer<typeof ReferralChild>;

const answerFields = {
  relationship: text(L.relationship),
  connectionNarrative: text(L.narrative),
  communityContribution: text(L.narrative),
  firstChoice: FirstChoice.default('unknown'),
  enrollmentInterest: text(L.text),
  additionalInformation: text(L.text),
};

const familyFields = {
  familyLastName: text(L.familyLastName),
  guardians: z
    .array(ReferralGuardian)
    .max(L.guardians, `At most ${L.guardians} parents or guardians`)
    .default([]),
  ...answerFields,
};

const uniqueChildKeys = (children: { childKey?: string }[], ctx: z.RefinementCtx) => {
  const seen = new Set<string>();
  children.forEach((c, i) => {
    if (!c.childKey) return;
    if (seen.has(c.childKey))
      ctx.addIssue({
        code: 'custom',
        path: ['children', i, 'childKey'],
        message: 'Duplicate child',
      });
    seen.add(c.childKey);
  });
};

const childrenInput = z
  .array(ReferralChildInput)
  .max(L.children, `At most ${L.children} children`)
  .default([]);

export const ReferralPayloadInput = z
  .object({ ...familyFields, children: childrenInput })
  .superRefine((p, ctx) => uniqueChildKeys(p.children, ctx));
export type ReferralPayloadInput = z.infer<typeof ReferralPayloadInput>;

export const ReferralPayload = z
  .object({ ...familyFields, children: z.array(ReferralChild).max(L.children) })
  .superRefine((p, ctx) => uniqueChildKeys(p.children, ctx));
export type ReferralPayload = z.infer<typeof ReferralPayload>;

export function emptyReferralGuardian(): ReferralGuardian {
  return { firstName: '', lastName: '', email: '', phone: '' };
}

export function emptyReferralChild(): ReferralChildInput {
  return ReferralChildInput.parse({});
}

export function emptyReferralPayload(): ReferralPayloadInput {
  return ReferralPayloadInput.parse({
    guardians: [emptyReferralGuardian(), emptyReferralGuardian()],
    children: [emptyReferralChild()],
  });
}

const filled = (...values: string[]) => values.some((v) => v.trim() !== '');

export function isBlankGuardian(g: ReferralGuardian): boolean {
  return !filled(g.firstName, g.lastName, g.email, g.phone);
}

type ChildValues = Omit<ReferralChildInput, 'childKey'>;

export function isBlankChild(c: ChildValues): boolean {
  return !filled(
    c.legalFirstName,
    c.preferredName,
    c.middleName,
    c.lastName,
    c.grade,
    c.currentSchool,
    c.city,
    c.stateOrCountry,
    c.academicContext,
    c.disciplinaryContext,
  );
}

export function hasAutosaveContent(p: {
  familyLastName: string;
  children: ChildValues[];
}): boolean {
  return (
    filled(p.familyLastName) ||
    p.children.some((c) => filled(c.legalFirstName, c.preferredName, c.middleName, c.lastName))
  );
}

export function normalizeReferralPayload(p: ReferralPayloadInput): ReferralPayloadInput {
  return {
    ...p,
    guardians: p.guardians.filter((g) => !isBlankGuardian(g)),
    children: p.children
      .filter((c) => !isBlankChild(c))
      .map((c) => ({
        ...c,
        academicContext: c.academicFamiliarity === 'yes' ? c.academicContext : '',
        disciplinaryContext: c.disciplinaryFamiliarity === 'yes' ? c.disciplinaryContext : '',
      })),
  };
}

export const ReferralStep = z.number().int().min(1).max(REFERRAL_STEP_COUNT);

export const ReferralCycleRef = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  referralsOpen: z.boolean(),
  endsOn: z.string(),
});
export type ReferralCycleRef = z.infer<typeof ReferralCycleRef>;

export const ReferralChildStage = z.object({
  childKey: z.string().uuid(),
  name: z.string(),
  stage: ApplicantStage,
});
export type ReferralChildStage = z.infer<typeof ReferralChildStage>;

export const ReferralClarification = z.object({
  request: z.string(),
  requestedAt: z.string(),
});
export type ReferralClarification = z.infer<typeof ReferralClarification>;

export const ReferralDto = z.object({
  id: z.string().uuid(),
  status: ReferralStatus,
  currentStep: ReferralStep,
  cycle: ReferralCycleRef.nullable(),
  payload: ReferralPayload,
  version: z.number().int(),
  countsTowardAllotment: z.boolean(),
  recommendationMarkedAt: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  submittedAt: z.string().nullable(),
  childStages: z.array(ReferralChildStage),
  clarification: ReferralClarification.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ReferralDto = z.infer<typeof ReferralDto>;

export const ReferralSummary = z.object({
  id: z.string().uuid(),
  status: ReferralStatus,
  currentStep: ReferralStep,
  cycle: ReferralCycleRef.nullable(),
  familyLastName: z.string(),
  childNames: z.array(z.string()),
  countsTowardAllotment: z.boolean(),
  recommendationMarkedAt: z.string().nullable(),
  cycleEnded: z.boolean(),
  referenceNumber: z.string().nullable(),
  submittedAt: z.string().nullable(),
  childStages: z.array(ReferralChildStage),
  clarification: ReferralClarification.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ReferralSummary = z.infer<typeof ReferralSummary>;

export const CreateReferralInput = z.object({
  cycleId: z.string().uuid().nullable().default(null),
  currentStep: ReferralStep.default(1),
  payload: ReferralPayloadInput,
  countsTowardAllotment: z.boolean().default(false),
});
export type CreateReferralInput = z.infer<typeof CreateReferralInput>;

export const UpdateReferralInput = z.object({
  version: z.number().int().min(1),
  cycleId: z.string().uuid().nullable().optional(),
  currentStep: ReferralStep.optional(),
  payload: ReferralPayloadInput.optional(),
  countsTowardAllotment: z.boolean().optional(),
});
export type UpdateReferralInput = z.infer<typeof UpdateReferralInput>;

export const SubmitReferralInput = z.object({
  version: z.number().int().min(1),
  acknowledged: z.boolean(),
});
export type SubmitReferralInput = z.infer<typeof SubmitReferralInput>;

export const SubmitReferralResult = z.object({
  alreadySubmitted: z.boolean(),
  referral: ReferralDto,
});
export type SubmitReferralResult = z.infer<typeof SubmitReferralResult>;

export const REFERRAL_INCOMPLETE = 'REFERRAL_INCOMPLETE';
export const REFERRAL_IS_DRAFT = 'REFERRAL_IS_DRAFT';
export const REFERRAL_ARCHIVED = 'REFERRAL_ARCHIVED';

export const EditReferralPayloadInput = z
  .object({ ...answerFields, children: childrenInput })
  .superRefine((p, ctx) => uniqueChildKeys(p.children, ctx));
export type EditReferralPayloadInput = z.infer<typeof EditReferralPayloadInput>;

export const EditReferralInput = z.object({
  version: z.number().int().min(1),
  payload: EditReferralPayloadInput,
  countsTowardAllotment: z.boolean(),
  resubmit: z.boolean().default(false),
});
export type EditReferralInput = z.infer<typeof EditReferralInput>;

export const REFERRAL_EDIT_KINDS = [
  'applicant_added',
  'applicant_info_updated',
  'screening_responses_updated',
  'family_responses_updated',
] as const;
export const ReferralEditKind = z.enum(REFERRAL_EDIT_KINDS);
export type ReferralEditKind = z.infer<typeof ReferralEditKind>;

export const EditReferralResult = z.object({
  changes: z.array(ReferralEditKind),
  resubmitted: z.boolean(),
  referral: ReferralDto,
});
export type EditReferralResult = z.infer<typeof EditReferralResult>;

export const CLARIFICATION_RESPONSE_MIN = 5;
export const CLARIFICATION_RESPONSE_MAX = 5000;
export const REFERRAL_NOT_AWAITING_CLARIFICATION = 'REFERRAL_NOT_AWAITING_CLARIFICATION';
export const CLARIFICATION_RESPONSE_REQUIRED = 'CLARIFICATION_RESPONSE_REQUIRED';

export const ClarificationResponseInput = z.object({
  version: z.number().int().min(1),
  response: z
    .string()
    .trim()
    .min(CLARIFICATION_RESPONSE_MIN, `Write at least ${CLARIFICATION_RESPONSE_MIN} characters`)
    .max(CLARIFICATION_RESPONSE_MAX, `At most ${CLARIFICATION_RESPONSE_MAX} characters`),
});
export type ClarificationResponseInput = z.infer<typeof ClarificationResponseInput>;

export const TRUSTEE_ACTIVITY_KINDS = ['created', 'submitted', 'status_changed', 'edited'] as const;
export const TrusteeActivityKind = z.enum(TRUSTEE_ACTIVITY_KINDS);
export type TrusteeActivityKind = z.infer<typeof TrusteeActivityKind>;

export const TrusteeActivityEvent = z.object({
  id: z.string(),
  kind: TrusteeActivityKind,
  at: z.string(),
  fromStatus: ReferralStatus.nullable(),
  toStatus: ReferralStatus.nullable(),
  changes: z.array(ReferralEditKind),
});
export type TrusteeActivityEvent = z.infer<typeof TrusteeActivityEvent>;

export const TrusteeReferralActivity = z.object({ events: z.array(TrusteeActivityEvent) });
export type TrusteeReferralActivity = z.infer<typeof TrusteeReferralActivity>;

export const ReferralSubmittedSnapshot = z.object({
  referenceNumber: z.string(),
  submittedAt: z.string(),
  acknowledgedAt: z.string(),
  cycle: z.object({ id: z.string().uuid(), code: z.string(), label: z.string() }),
  trustee: z.object({ id: z.string().uuid(), displayName: z.string(), email: z.string() }),
  payload: ReferralPayload,
});
export type ReferralSubmittedSnapshot = z.infer<typeof ReferralSubmittedSnapshot>;

export function childDisplayName(
  c: Pick<ReferralChildInput, 'legalFirstName' | 'preferredName' | 'lastName'>,
): string {
  return [c.preferredName.trim() || c.legalFirstName.trim(), c.lastName.trim()]
    .filter(Boolean)
    .join(' ');
}

export const AllotmentCycleUsage = z.object({
  cycle: ReferralCycleRef,
  ended: z.boolean(),
  used: z.number().int(),
  activeReferralIds: z.array(z.string().uuid()),
});
export type AllotmentCycleUsage = z.infer<typeof AllotmentCycleUsage>;

export const RecommendationAllotment = z.object({
  limit: z.number().int(),
  cycles: z.array(AllotmentCycleUsage),
});
export type RecommendationAllotment = z.infer<typeof RecommendationAllotment>;
